import React from 'react';
import type { ClipDataPoint, BackendDiaper, FirmwareConstants } from '../../lib/api-clip-monitor';
import type { DiaperModel, Criterion } from '../../lib/types';

interface DiaperModelListItem {
  name: string;
  code: number;
}

interface BaselineInfo {
  adcValues: number[];
  timestamp: Date;
  recordIndex: number;
}

interface DiaperAge {
  hours: number;
  minutes: number;
  seconds: number;
  totalMs: number;
}

interface DiaperModelInfoProps {
  overriddenDiaperModelCode: number | null;
  setOverriddenDiaperModelCode: (code: number | null) => void;
  diaperModels: DiaperModelListItem[];
  localModelOverrides: DiaperModelListItem[];
  hasInvalidDiaperAlert: boolean;
  currentDiaperModel: DiaperModel | null;
  baselineInfo: BaselineInfo | null;
  diaperAge: DiaperAge | null;
  filteredByDiaperCounter: ClipDataPoint[];
  firmwareConstants: FirmwareConstants | null;
  selectedDiaperCounter: number | null;
  backendDiaperByCounter: Record<number, BackendDiaper>;
  currentCriterion: Criterion | null;
}

export const DiaperModelInfo: React.FC<DiaperModelInfoProps> = ({
  overriddenDiaperModelCode,
  setOverriddenDiaperModelCode,
  diaperModels,
  localModelOverrides,
  hasInvalidDiaperAlert,
  currentDiaperModel,
  baselineInfo,
  diaperAge,
  filteredByDiaperCounter,
  firmwareConstants,
  selectedDiaperCounter,
  backendDiaperByCounter,
  currentCriterion,
}) => {
  const bd = selectedDiaperCounter !== null ? backendDiaperByCounter[selectedDiaperCounter] : null;

  return (
    <div className="md:w-1/2">
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-semibold text-gray-700">Diaper Model</label>
        <select
          value={overriddenDiaperModelCode || ''}
          onChange={(e) => setOverriddenDiaperModelCode(e.target.value ? parseInt(e.target.value, 10) : null)}
          className="text-xs border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="">Auto-detect</option>
          <optgroup label="Backend Models">
            {diaperModels.map(m => (
              <option key={m.code} value={m.code}>{m.name} ({m.code})</option>
            ))}
          </optgroup>
          {localModelOverrides.length > 0 && (
            <optgroup label="Experimental Overrides">
              {localModelOverrides.map(m => (
                <option key={m.code} value={m.code}>🧪 {m.name}</option>
              ))}
            </optgroup>
          )}
        </select>
      </div>

      {hasInvalidDiaperAlert && overriddenDiaperModelCode === null ? (
        <div className="bg-red-100 border-2 border-red-500 rounded-lg p-3">
          <p className="text-red-800 font-bold text-sm">🚨 INVALID DIAPER</p>
          <p className="text-red-700 text-xs mt-1">Model 0 or unknown. Select a model override above.</p>
        </div>
      ) : currentDiaperModel ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-2">
          <div>
            <p className="text-green-800 font-semibold text-sm">
              {overriddenDiaperModelCode !== null ? '🔧 ' : '✓ '}{currentDiaperModel.name}
            </p>
            <p className="text-green-700 text-xs mt-1">
              Model {currentDiaperModel.code} {overriddenDiaperModelCode === null && '(Auto-detected)'} • {Math.round(currentDiaperModel.capacity)}mL
            </p>
          </div>

          {baselineInfo && diaperAge && (
            <div className="pt-2 border-t border-green-300">
              <p className="text-green-800 text-xs font-semibold mb-1">📊 Baseline (Initial Dry ADC):</p>
              <p className="text-green-700 text-xs">• ADC Values: [{baselineInfo.adcValues.join(', ')}]</p>
              <p className="text-green-700 text-xs mt-2">
                • <strong>Start Time:</strong> {baselineInfo.timestamp.toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', day: '2-digit', month: '2-digit' })}
              </p>
              {filteredByDiaperCounter.length > 0 && (
                <p className="text-green-700 text-xs">
                  • <strong>Latest Time:</strong> {new Date(filteredByDiaperCounter[filteredByDiaperCounter.length - 1].timestamp).toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', day: '2-digit', month: '2-digit' })}
                </p>
              )}
              <p className="text-green-700 text-xs">
                • <strong>Duration:</strong> {diaperAge.hours}h {diaperAge.minutes}m {diaperAge.seconds}s
              </p>
            </div>
          )}

          {currentDiaperModel.leakThreshold && (
            <div className="pt-2 border-t border-green-300">
              <p className="text-green-800 text-xs font-semibold mb-1">Leak Threshold:</p>
              <p className="text-green-700 text-xs">• Wetness: {currentDiaperModel.leakThreshold.wetnessLevel.toLocaleString()}</p>
              <p className="text-green-700 text-xs">• ADC: [{currentDiaperModel.leakThreshold.adcValues.join(', ')}]</p>
            </div>
          )}

          {firmwareConstants && (() => {
            const fwModel = firmwareConstants.models[String(currentDiaperModel.code)];
            if (!fwModel) return null;
            return (
              <details className="pt-2 border-t border-green-300">
                <summary className="text-green-800 text-xs font-semibold cursor-pointer">
                  Firmware Constants (FW v{firmwareConstants.firmwareVersion})
                </summary>
                <div className="mt-1 space-y-1">
                  <p className="text-green-700 text-xs">
                    • <strong>Pooling Threshold:</strong> {fwModel.poolingThreshold} h{'\u03A9'}
                    {fwModel.poolingEffectivelyDisabled
                      ? <span className="ml-1 text-orange-600 font-semibold">(Effectively disabled)</span>
                      : <span className="ml-1 text-green-600">(Active)</span>}
                  </p>
                  {fwModel.poolingEffectivelyDisabled && (
                    <p className="text-orange-600 text-xs bg-orange-50 rounded px-1 py-0.5">
                      Pooling status will rarely/never fire for this model ({fwModel.poolingThreshold} h{'\u03A9'} threshold).
                    </p>
                  )}
                  {fwModel.sensorHighValues10bit && (
                    <>
                      <p className="text-green-700 text-xs">• <strong>High Values (10-bit):</strong> [{fwModel.sensorHighValues10bit.join(', ')}]</p>
                      <p className="text-green-700 text-xs">• <strong>High Values (8-bit):</strong> [{fwModel.sensorHighValues8bit?.join(', ')}]</p>
                    </>
                  )}
                  {fwModel.lineLengthMm != null && (
                    <p className="text-green-700 text-xs">• <strong>Sensor Geometry:</strong> {fwModel.lineLengthMm}mm x {fwModel.lineSeparationMm}mm</p>
                  )}
                </div>
              </details>
            );
          })()}
        </div>
      ) : (
        <div className="bg-gray-100 border border-gray-300 rounded-lg p-3">
          <p className="text-gray-600 text-sm">No model detected</p>
        </div>
      )}

      {bd && (
        <BackendGroundTruth
          bd={bd}
          currentDiaperModel={currentDiaperModel}
          currentCriterion={currentCriterion}
        />
      )}
    </div>
  );
};

const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
const fmtDateTime = (iso: string) => new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });

const BackendGroundTruth: React.FC<{
  bd: BackendDiaper;
  currentDiaperModel: DiaperModel | null;
  currentCriterion: Criterion | null;
}> = ({ bd, currentDiaperModel, currentCriterion }) => {
  const hasAlerts = bd.alerts && Object.values(bd.alerts).some(v => v != null);

  const warnings = React.useMemo(() => {
    const result: Array<{ severity: 'red' | 'yellow'; msg: string; detail?: string }> = [];
    const bdm = bd.diaper_model;
    const bdc = bd.criterion;

    if (bdm && currentDiaperModel) {
      const metaCoeffs = bdm.coefficients;
      const curCoeffs = [currentDiaperModel.c0, currentDiaperModel.c1, currentDiaperModel.c2, currentDiaperModel.c3];
      const coeffsDiffer = metaCoeffs.length === curCoeffs.length
        ? metaCoeffs.some((c, i) => c !== curCoeffs[i])
        : true;
      if (coeffsDiffer) {
        result.push({
          severity: 'red',
          msg: 'Polynomial coefficients have changed since this diaper was monitored. The volume curve uses current values, not the ones active at the time.',
          detail: `Then: [${metaCoeffs.map(c => c.toExponential(4)).join(', ')}]\nNow:  [${curCoeffs.map(c => c.toExponential(4)).join(', ')}]`,
        });
      }
      if (bdm.capacity_ml !== currentDiaperModel.capacity) {
        result.push({ severity: 'red', msg: `Capacity changed: was ${bdm.capacity_ml} mL, now ${currentDiaperModel.capacity} mL. All % calculations use current value.` });
      }
      if (bdm.leak_threshold && currentDiaperModel.leakThreshold) {
        const wlDiff = bdm.leak_threshold.wetnessLevel !== currentDiaperModel.leakThreshold.wetnessLevel;
        const adcDiff = bdm.leak_threshold.adcValues.some((v, i) => v !== currentDiaperModel.leakThreshold!.adcValues[i]);
        if (wlDiff || adcDiff) {
          result.push({
            severity: 'yellow',
            msg: `Leak threshold changed.${wlDiff ? ` Wetness: ${bdm.leak_threshold.wetnessLevel} -> ${currentDiaperModel.leakThreshold.wetnessLevel}.` : ''}${adcDiff ? ` ADC values differ.` : ''}`,
          });
        }
      }
    }

    if (bdc && currentCriterion) {
      if (bdc.criterion_id !== currentCriterion.id) {
        result.push({ severity: 'yellow', msg: `Different criterion was active: "${bdc.name}" (ID ${bdc.criterion_id}) vs current "${currentCriterion.name}" (ID ${currentCriterion.id}).` });
      } else {
        const metaCfg = bdc.config;
        const curCfg = currentCriterion.content;
        if (metaCfg.stabilizationPeriod_sec !== curCfg.stabilizationPeriod_sec) {
          result.push({ severity: 'yellow', msg: `Stabilization period changed: was ${metaCfg.stabilizationPeriod_sec}s, now ${curCfg.stabilizationPeriod_sec}s.` });
        }
        const metaPhases: any[] = metaCfg.phases || [];
        const curPhases = curCfg.phases || [];
        for (const mp of metaPhases) {
          const cp = curPhases.find((p: any) => p.id === mp.id);
          if (!cp) continue;
          const mt = mp.thresholds?.[0];
          const ct = cp.thresholds?.[0];
          if (mt && ct && mt.percent !== ct.percent) {
            result.push({ severity: 'yellow', msg: `Phase "${mp.name || mp.id}" threshold changed: was ${mt.percent}%, now ${ct.percent}%.` });
          }
        }
      }
    }

    return result;
  }, [bd, currentDiaperModel, currentCriterion]);

  return (
    <details className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 mt-3" open>
      <summary className="text-indigo-800 text-sm font-semibold cursor-pointer">
        Backend Ground Truth (ID: {bd.diaper_id})
      </summary>
      <div className="mt-2 space-y-2">
        <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
          <span className="text-gray-600">Start:</span>
          <span className="font-medium">{fmtDateTime(bd.start_time)}</span>
          <span className="text-gray-600">End:</span>
          <span className="font-medium">{fmtDateTime(bd.end_time)}</span>
          <span className="text-gray-600">Closing Volume:</span>
          <span className="font-medium">{Math.round(bd.closing_volume)} mL ({bd.closing_percent.toFixed(1)}%)</span>
          <span className="text-gray-600">Closing Level:</span>
          <span className="font-medium">{bd.closing_level.toLocaleString()}</span>
        </div>

        {bd.emictions.length > 0 && (
          <div className="pt-2 border-t border-indigo-200">
            <p className="text-indigo-800 text-xs font-semibold mb-1">Emictions ({bd.emictions.length}):</p>
            <div className="space-y-0.5">
              {bd.emictions.map((e, i) => (
                <p key={i} className="text-xs text-indigo-700">
                  {fmtTime(e.time)} — +{Math.round(e.delta_ml)} mL (total: {Math.round(e.cumulative_ml)} mL)
                  {e.leak && <span className="ml-1 text-red-600 font-bold">LEAK</span>}
                </p>
              ))}
            </div>
          </div>
        )}

        {hasAlerts && (
          <div className="pt-2 border-t border-indigo-200">
            <p className="text-indigo-800 text-xs font-semibold mb-1">Backend Alerts:</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
              {bd.alerts.wet_alert_time && (<><span className="text-yellow-700">Wet Alert:</span><span className="font-medium">{fmtTime(bd.alerts.wet_alert_time)}</span></>)}
              {bd.alerts.leak_alert_time && (<><span className="text-red-700">Leak Alert:</span><span className="font-medium">{fmtTime(bd.alerts.leak_alert_time)}</span></>)}
              {bd.alerts.pool_alert_time && (<><span className="text-orange-700">Pool Alert:</span><span className="font-medium">{fmtTime(bd.alerts.pool_alert_time)}</span></>)}
              {bd.alerts.pool_time && (<><span className="text-gray-600">Pool Detected:</span><span className="font-medium">{fmtTime(bd.alerts.pool_time)}</span></>)}
              {bd.alerts.leak_time && (<><span className="text-gray-600">Leak Detected:</span><span className="font-medium">{fmtTime(bd.alerts.leak_time)}</span></>)}
              {bd.alerts.remove_pool_time && (<><span className="text-gray-600">Pool Cleared:</span><span className="font-medium">{fmtTime(bd.alerts.remove_pool_time)}</span></>)}
            </div>
          </div>
        )}

        {!hasAlerts && bd.emictions.length === 0 && (
          <p className="text-indigo-600 text-xs pt-1">No alerts or emictions recorded by backend</p>
        )}

        {warnings.length > 0 ? (
          <div className="pt-2 border-t border-indigo-200 space-y-1.5">
            {warnings.map((w, i) => (
              <div
                key={i}
                className={`text-xs rounded p-1.5 ${w.severity === 'red' ? 'bg-red-100 border border-red-400 text-red-800' : 'bg-yellow-50 border border-yellow-300 text-yellow-800'}`}
              >
                <p className="font-semibold">{w.severity === 'red' ? 'Warning' : 'Info'}: {w.msg}</p>
                {w.detail && <pre className="mt-0.5 text-[10px] whitespace-pre-wrap opacity-80">{w.detail}</pre>}
              </div>
            ))}
          </div>
        ) : (bd.diaper_model || bd.criterion) ? (
          <div className="pt-2 border-t border-indigo-200">
            <p className="text-green-700 text-xs font-semibold">Consistent -- model and criterion match current values</p>
          </div>
        ) : null}

        {(bd.diaper_model || bd.criterion) && (
          <details className="pt-2 border-t border-indigo-200">
            <summary className="text-indigo-700 text-xs cursor-pointer font-semibold">
              Historical snapshot (model + criterion at time)
            </summary>
            <div className="mt-1 space-y-1.5 text-xs">
              {bd.diaper_model && (
                <div>
                  <p className="text-gray-700 font-semibold">Model: {bd.diaper_model.name} (code {bd.diaper_model.model_code})</p>
                  <p className="text-gray-600">Capacity: {bd.diaper_model.capacity_ml} mL</p>
                  <p className="text-gray-600">Coefficients: [{bd.diaper_model.coefficients.map(c => c.toExponential(4)).join(', ')}]</p>
                  {bd.diaper_model.leak_threshold && (
                    <p className="text-gray-600">
                      Leak: wetness {bd.diaper_model.leak_threshold.wetnessLevel.toLocaleString()}, ADC [{bd.diaper_model.leak_threshold.adcValues.join(', ')}]
                    </p>
                  )}
                </div>
              )}
              {bd.criterion && (
                <div>
                  <p className="text-gray-700 font-semibold">Criterion: {bd.criterion.name} (ID {bd.criterion.criterion_id})</p>
                  {bd.criterion.config.phases && (
                    <div className="text-gray-600">
                      {(bd.criterion.config.phases as any[]).filter((p: any) => p.name).map((p: any) => (
                        <p key={p.id}>
                          Phase "{p.name}": {p.thresholds?.[0]?.percent ?? '?'}%
                          {p.offMonitoring ? ' (off monitoring)' : ''}
                        </p>
                      ))}
                    </div>
                  )}
                  {bd.criterion.config.stabilizationPeriod_sec != null && (
                    <p className="text-gray-600">Stabilization: {bd.criterion.config.stabilizationPeriod_sec}s</p>
                  )}
                  {bd.criterion.config.schedule?.timeZone && (
                    <p className="text-gray-600">Timezone: {bd.criterion.config.schedule.timeZone}</p>
                  )}
                </div>
              )}
            </div>
          </details>
        )}
      </div>
    </details>
  );
};
